/**
 * Вход для пользователей знакомств.
 * Регистрации здесь нет: аккаунт создаётся в конце анкеты-онбординга,
 * поэтому внизу — кнопка «Создать анкету».
 */
import { useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { motion } from 'framer-motion';

import AboutLink from '../components/Auth/AboutLink';
import SocialAuthButtons from '../components/Auth/SocialAuthButtons';
import { LayoutContext } from '../context';
import { APIAuthLogin } from '../api_auth';
import useAuthSuccess from '../components/Auth/useAuthSuccess';
import { getLastRoute } from '../lastRoute';
import {
    AuthBackground, Logo, Wordmark, Field, PrimaryButton, TextLink, Notice, PasswordToggle,
    IconMail, IconLock,
} from '../components/Auth/ui';

export default function LoginPage() {
    const navigate = useNavigate();
    const { token } = useContext(LayoutContext);
    const applyAuth = useAuthSuccess();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Уже вошли (например, открыли /login из приложения) — возвращаем в ленту
    if (token) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e?.preventDefault();
        setError(null);
        if (!email.trim() || !password) { setError('Введите email и пароль'); return; }
        setLoading(true);
        try {
            const response = await APIAuthLogin({ email: email.trim(), password });
            applyAuth(response, 'email');
            // Возвращаем человека туда, где он закончил в прошлый раз
            navigate(getLastRoute() || '/', { replace: true });
        } catch (err) {
            setError(err?.response?.data?.error || 'Не удалось войти. Проверьте интернет и попробуйте ещё раз');
            setLoading(false);
        }
    };

    return (
        <AuthBackground>
            <div className="lv-screen lv-login">
                <motion.div className="lv-login__brand"
                            initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}>
                    <Logo size="lg" />
                    <Wordmark size="lg" />
                    <div className="lv-login__tag">Знакомства для искренних отношений</div>
                </motion.div>

                <motion.form className="lv-panel" onSubmit={submit} noValidate
                             initial={{ opacity: 0, y: 26, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                             transition={{ delay: 0.12, type: 'spring', stiffness: 260, damping: 26 }}>
                    <h2 className="lv-panel__title">С возвращением</h2>

                    {error ? <Notice tone="error">{error}</Notice> : null}

                    <Field icon={<IconMail />} type="email" inputMode="email" autoComplete="email"
                           placeholder="Email" value={email} enterKeyHint="next"
                           onChange={(e) => setEmail(e.target.value)} />

                    <Field icon={<IconLock />} type={showPw ? 'text' : 'password'} autoComplete="current-password"
                           placeholder="Пароль" value={password} enterKeyHint="go"
                           onChange={(e) => setPassword(e.target.value)}
                           end={<PasswordToggle shown={showPw} onToggle={() => setShowPw((s) => !s)} />} />

                    <TextLink small className="lv-login__forgot" onClick={() => navigate('/forgot-password')}>
                        Забыли пароль?
                    </TextLink>

                    <PrimaryButton type="submit" loading={loading}>
                        {loading ? 'Входим…' : 'Войти'}
                    </PrimaryButton>
                    <SocialAuthButtons />
                </motion.form>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                    <div className="lv-divider">Ещё нет анкеты?</div>
                    <PrimaryButton variant="ghost" onClick={() => navigate('/')}>Создать анкету</PrimaryButton>
                </motion.div>
                <AboutLink />
            </div>
        </AuthBackground>
    );
}
